/**
 * Who Krevya is, in one place.
 *
 * The landing page's contact block, its footer and every legal page read from
 * here, so the company's name, its address and how to reach it can only ever
 * say one thing. A legal text that names one address while the footer names
 * another is worse than either alone.
 *
 * `email` is where people write to Krevya, formal KVKK applications
 * included. It has to be a mailbox someone reads: an application sent to an
 * address that bounces is one the controller never received. The postal
 * address stays beside it as the channel the law accepts on its own.
 */
export const COMPANY = {
  /** The legal entity behind Krevya: the data controller and the contracting party. */
  legalName: 'AUTOEER LLC',
  brand: 'Krevya',
  /** Where it is registered, as a phrase a contract can use. */
  registeredIn: { en: 'a limited liability company organised in Wyoming, USA', tr: "ABD'nin Wyoming eyaletinde kurulmuş bir limited şirket (LLC)" },
  governingLaw: { en: 'the laws of the State of Wyoming, USA', tr: "ABD'nin Wyoming eyaleti hukuku" },
  address: '30 N Gould St Ste 58362, Sheridan, WY 82801, USA',
  // Non-breaking spaces: a number that wraps in the middle cannot be read off the screen.
  phone: '+90 539 735 98 53',
  phoneHref: 'tel:+905397359853',
  email: 'support@krevya.com',
  /** The founder's own mailbox, offered beside support on the contact block. */
  founderEmail: 'umut@krevya.com',
  /** WhatsApp on the same number as `phone`; the prefilled text is added per language on the page. */
  whatsappHref: 'https://wa.me/905397359853',
  site: 'https://krevya.com',
  /**
   * Where Krevya posts publicly. Named here because the same handle has to
   * appear in the structured data on the site as well: a search engine builds
   * one entity out of a brand only when its profiles point at each other.
   */
  instagram: 'https://www.instagram.com/getkrevya',
  instagramHandle: '@getkrevya',
  founder: {
    name: 'Umut Berkay Guler',
    title: { en: 'Founder of Krevya', tr: "Krevya'nın kurucusu" },
    /**
     * The role as a company role rather than a greeting: the about page states
     * it this way, and an application form or a profile that names the same
     * role is the point of having it written down once.
     */
    role: { en: 'Founder & CEO', tr: 'Kurucu ve CEO' },
    linkedin: 'https://www.linkedin.com/in/umut-berkay-guler/',
    photo: '/team/founder.webp',
  },
} as const;

/** The date every legal text below was last changed, written once. */
export const LEGAL_UPDATED = '2026-10-07';

/**
 * Where "Contact us" on the Partner plan goes: a WhatsApp chat with the
 * message already written, since Partner is arranged in a conversation and
 * not at a checkout. Mail is the way in when there is no WhatsApp number.
 */
export function partnerContactHref(
  message: string,
  channels: { whatsappHref: string; email: string } = COMPANY,
): string {
  const text = encodeURIComponent(message);
  if (channels.whatsappHref) return `${channels.whatsappHref}?text=${text}`;
  return `mailto:${channels.email}?subject=${encodeURIComponent('Krevya Partner')}&body=${text}`;
}
